import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, Target as TargetIcon, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHead } from "@/components/Bits";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { SalesTargets } from "@/components/SalesTargets";
import { MmChecklist } from "@/components/MmChecklist";

const ROLE_OPTS = [
  { v: "central_marketing", l: "Central Marketing Manager" },
  { v: "sales_manager_products", l: "B2B Product Sales Manager" },
  { v: "crm", l: "Central CRM" },
];
const thisMonth = () => new Date().toISOString().slice(0, 7);
const fmtVal = (t, v) =>
  t.unit === "₹" || t.metric === "b2b_revenue" ? money(v) : `${v}${t.unit ? " " + t.unit : ""}`;

export default function RoleTargets() {
  const { user } = useAuth();
  const isCEO = ["network_admin", "ceo"].includes(user.role);
  const [month, setMonth] = useState(thisMonth());
  const [targets, setTargets] = useState([]);
  const [metrics, setMetrics] = useState([]);
  const [form, setForm] = useState(null);

  const load = useCallback(() => {
    api.get("/role-targets", { params: { month } }).then(({ data }) => setTargets(data));
  }, [month]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/role-target-metrics").then(({ data }) => setMetrics(data)); }, []);

  const openNew = () =>
    setForm({ role: "central_marketing", name: "", goal: "", unit: "", metric: "manual", manual_value: "" });

  const save = async () => {
    if (!form.name.trim() || !(Number(form.goal) > 0)) {
      toast.error("Enter a target name and a numeric goal");
      return;
    }
    try {
      const payload = {
        role: form.role, name: form.name.trim(), goal: Number(form.goal),
        unit: form.unit.trim(), month, metric: form.metric,
        manual_value: form.metric === "manual" ? Number(form.manual_value || 0) : 0,
      };
      if (form.id) await api.put(`/role-targets/${form.id}`, payload);
      else await api.post("/role-targets", payload);
      toast.success("Target saved");
      setForm(null);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const del = async (t) => {
    try { await api.delete(`/role-targets/${t.id}`); toast.success("Target removed"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const updateManual = async (t, value) => {
    try {
      await api.put(`/role-targets/${t.id}`, { manual_value: Number(value || 0) });
      toast.success("Achievement updated");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const grouped = ROLE_OPTS
    .map((r) => ({ ...r, items: targets.filter((t) => t.role === r.v) }))
    .filter((g) => isCEO || g.items.length);
  const roleMetrics = form ? metrics.filter((m) => m.roles.includes(form.role)) : [];

  return (
    <div>
      <PageHead
        title="Monthly Targets"
        subtitle={isCEO ? "Set and track targets for each central role" : "Your targets for the month"}
      />

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
          className="h-10 w-44" data-testid="target-month-picker" />
        {isCEO && (
          <Button onClick={openNew} data-testid="add-target-btn">
            <Plus className="mr-1.5 h-4 w-4" /> Add target
          </Button>
        )}
      </div>

      {grouped.length === 0 && (
        <p className="text-slate-500" data-testid="no-targets">No targets set for this month.</p>
      )}

      <div className="space-y-8">
        {grouped.map((g) => (
          <div key={g.v} data-testid={`target-group-${g.v}`}>
            <h3 className="mb-3 flex items-center gap-2 font-display text-lg font-semibold text-brand-ink">
              <TargetIcon className="h-4 w-4 text-brand-pink" /> {g.l}
            </h3>
            {g.items.length === 0 ? (
              <p className="text-sm text-slate-400">No targets yet.</p>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {g.items.map((t) => {
                  const pct = Math.min(100, t.pct);
                  const bar = t.pct >= 100 ? "bg-emerald-500" : t.pct >= 50 ? "bg-amber-500" : "bg-brand-pink";
                  const canEditManual = t.metric === "manual" && (isCEO || user.role === t.role);
                  return (
                    <Card key={t.id} className="border-brand-line shadow-none" data-testid={`target-card-${t.id}`}>
                      <CardHeader className="flex-row items-start justify-between space-y-0 pb-2">
                        <div>
                          <CardTitle className="text-base">{t.name}</CardTitle>
                          <Badge className="mt-1 border-0 bg-slate-100 text-[10px] text-slate-600">
                            {t.auto ? "Auto-tracked" : "Manual"}
                          </Badge>
                        </div>
                        {isCEO && (
                          <div className="flex gap-1">
                            <button data-testid={`edit-target-${t.id}`} className="text-slate-400 hover:text-brand-ink"
                              onClick={() => setForm({ ...t, goal: String(t.goal), unit: t.unit || "",
                                manual_value: String(t.manual_value ?? "") })}>
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button data-testid={`delete-target-${t.id}`} className="text-rose-400 hover:text-rose-600"
                              onClick={() => del(t)}>
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-end justify-between">
                          <span className="font-display text-2xl font-semibold text-brand-ink" data-testid={`target-current-${t.id}`}>
                            {fmtVal(t, t.current)}
                          </span>
                          <span className="text-sm text-slate-500">of {fmtVal(t, t.goal)}</span>
                        </div>
                        <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-100">
                          <div className={`h-full ${bar}`} style={{ width: `${pct}%` }} />
                        </div>
                        <div className="mt-2 flex items-center justify-between text-xs">
                          <span className="font-semibold text-brand-ink" data-testid={`target-pct-${t.id}`}>{t.pct}% achieved</span>
                          <span className="flex items-center gap-1 text-slate-500" data-testid={`target-lastmonth-${t.id}`}>
                            <TrendingUp className="h-3 w-3" /> Last month: {fmtVal(t, t.last_month)}
                          </span>
                        </div>
                        {canEditManual && (
                          <div className="mt-3 flex gap-2">
                            <Input type="number" defaultValue={t.manual_value ?? ""} className="h-9"
                              data-testid={`manual-input-${t.id}`} id={`mi-${t.id}`} placeholder="Update achievement" />
                            <Button size="sm" variant="outline" data-testid={`manual-save-${t.id}`}
                              onClick={() => updateManual(t, document.getElementById(`mi-${t.id}`).value)}>
                              Update
                            </Button>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>

      {(isCEO || ["central_sales_team"].includes(user.role)) && (
        <div className="mt-10" data-testid="sales-targets-section">
          <SalesTargets />
        </div>
      )}
      {(isCEO || user.role === "central_marketing") && (
        <div className="mt-10" data-testid="mm-targets-section">
          <MmChecklist />
        </div>
      )}

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="sm:max-w-md" data-testid="target-dialog">
          <DialogHeader>
            <DialogTitle>{form?.id ? "Edit target" : "New target"}</DialogTitle>
          </DialogHeader>
          {form && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v, metric: "manual" }))}
                  disabled={!!form.id}>
                  <SelectTrigger className="mt-1" data-testid="target-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROLE_OPTS.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Target name</Label>
                <Input className="mt-1" data-testid="target-name-input" value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
              </div>
              <div className="flex gap-3">
                <div className="flex-1">
                  <Label className="text-xs">Goal</Label>
                  <Input className="mt-1" type="number" data-testid="target-goal-input" value={form.goal}
                    onChange={(e) => setForm((f) => ({ ...f, goal: e.target.value }))} />
                </div>
                <div className="flex-1">
                  <Label className="text-xs">Unit</Label>
                  <Input className="mt-1" placeholder="leads / orders…" data-testid="target-unit-input" value={form.unit}
                    onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value }))} />
                </div>
              </div>
              <div>
                <Label className="text-xs">Tracking source</Label>
                <Select value={form.metric} onValueChange={(v) => setForm((f) => ({ ...f, metric: v }))}>
                  <SelectTrigger className="mt-1" data-testid="target-metric-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {roleMetrics.map((m) => (
                      <SelectItem key={m.key} value={m.key}>{m.label}{m.auto ? " (auto)" : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {form.metric === "manual" && (
                <div>
                  <Label className="text-xs">Current achievement</Label>
                  <Input className="mt-1" type="number" data-testid="target-manual-input" value={form.manual_value}
                    onChange={(e) => setForm((f) => ({ ...f, manual_value: e.target.value }))} />
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)}>Cancel</Button>
            <Button onClick={save} data-testid="save-target-btn">Save target</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
